import { keywordLangs } from "@/components/suggestions/keywordLangs";

import type { KeywordLang, LocaleKeywords } from "@corca/data";

type RawKeywords = string[];

/**
 * Normalizes keywords from an array of strings to a map of keywords and it's priorities
 */
export function normalizeKeywords(...args: (RawKeywords | undefined)[]) {
  const keywords = args.reduce<Record<string, number>>((acc, k) => {
    if (!k) return acc;

    if (Array.isArray(k)) {
      return k.reduce((a, v) => {
        // Parse weight suffix: if keyword contains "^", split by last "^" and parse weight
        const lastCaretIndex = v.lastIndexOf("^");
        let keyword = v;
        let weight = 1;

        if (lastCaretIndex !== -1) {
          const potentialWeight = v.slice(lastCaretIndex + 1);
          const parsedWeight = Number(potentialWeight);

          // Only treat as weight if it's a valid number
          if (potentialWeight.length > 0 && !Number.isNaN(parsedWeight)) {
            keyword = v.slice(0, lastCaretIndex).trim();
            weight = parsedWeight;
          }
        }

        const trimmedKeyword = keyword.trim().replace(/\s+/g, " ");

        if (trimmedKeyword.length > 0) {
          a[trimmedKeyword] = weight;
          a[trimmedKeyword.replace(/ /g, "")] = weight;
        }
        return a;
      }, acc);
    }
    return Object.keys(k).reduce((a, v) => {
      if (k[v] != null) {
        a[v] = k[v];
        a[v.replace(/ /g, "")] = k[v];
      }
      return a;
    }, acc);
  }, {});

  return keywords;
}

type ObjWithKeywords = {
  keywords?: RawKeywords;
} & {
  [k in `keywords_${Exclude<KeywordLang, "en">}`]?: RawKeywords;
};

export function normalizeSuggestionKeywords(...objs: (ObjWithKeywords | undefined)[]) {
  return keywordLangs.reduce<Partial<LocaleKeywords>>((acc, lang) => {
    if (lang === "en") {
      acc[lang] = normalizeKeywords(...objs.map((s) => s?.keywords));
    } else {
      const key = `keywords_${lang}` as keyof ObjWithKeywords;
      acc[lang] = normalizeKeywords(...objs.map((s) => s?.[key]));
    }
    return acc;
  }, {}) as LocaleKeywords;
}
